import clsx from 'clsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRemainingTime } from './EnglishProficiencyTest.helpers';
import styles from './EnglishProficiencyAudioPlayer.module.scss';

interface IEnglishProficiencyAudioPlayer {
  /** A presigned URL, valid for the whole sitting. */
  src: string;
  /** Question number, used to label the control for screen readers. */
  questionIndex: number;
}

/**
 * The listening items' player.
 *
 * Built here rather than reached for: there is no `<audio>` anywhere else in
 * this app and none in the design system. Deliberately plain — play/pause, a
 * seekable bar, elapsed and total — because the candidate is listening to it,
 * not looking at it.
 *
 * Replays are unlimited. The clock is the only limit on a listening item, and
 * rationing plays would measure memory rather than comprehension.
 *
 * `preload="none"` matters: all sixty items are on one page and twelve of them
 * carry audio, so preloading would pull twelve files the moment the sitting
 * opens, on a connection the candidate may be about to need.
 */
const EnglishProficiencyAudioPlayer: FC<IEnglishProficiencyAudioPlayer> = ({
  src,
  questionIndex,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handleLoadedMetadata = () =>
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleError = () => {
      setIsPlaying(false);
      setHasFailed(true);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      void audio.play().catch(() => setHasFailed(true));
    } else {
      audio.pause();
    }
  }, []);

  const handleSeek = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }
      const nextTime = Number(event.target.value);
      audio.currentTime = nextTime;
      setCurrentTime(nextTime);
    },
    []
  );

  return (
    <div className={styles.player} data-testid="english-proficiency--audio">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the audio is
          the question; a transcript would be the answer. */}
      <audio ref={audioRef} src={src} preload="none" />
      <button
        type="button"
        className={styles.playButton}
        onClick={togglePlayback}
        disabled={hasFailed}
        aria-label={t(
          isPlaying
            ? 'englishProficiencyTest.audio.pause'
            : 'englishProficiencyTest.audio.play',
          { questionNumber: questionIndex }
        )}
      >
        <span
          className={clsx(styles.playIcon, {
            [styles.pauseIcon]: isPlaying,
          })}
          aria-hidden="true"
        />
      </button>
      <input
        type="range"
        className={styles.scrubber}
        min={0}
        max={duration || 0}
        step={0.1}
        value={Math.min(currentTime, duration || 0)}
        onChange={handleSeek}
        disabled={hasFailed || !duration}
        aria-label={t('englishProficiencyTest.audio.seek')}
      />
      <span className={styles.time}>
        {formatRemainingTime(currentTime)} / {formatRemainingTime(duration)}
      </span>
      {hasFailed ? (
        <p className={styles.error} role="alert">
          {t('englishProficiencyTest.audio.failed')}
        </p>
      ) : null}
    </div>
  );
};

export default EnglishProficiencyAudioPlayer;
